import moment from 'moment';
import { sortBy } from 'lodash-es';
import * as AssetDetailsActions from '../../../../state/asset-details/asset-details.actions';

import template from './asset-data.component.html';
import { isAccessDataPoint } from '../../../../functions/asset.functions';

const EVENT_TRACKING_NAME = 'AssetDetailData';
const EVENT_TRACKING_ACTIONS = {
  exportCSVClicked: 'exportCSVClicked',
  toggleSerie: 'toggleSerie',
  updateRange: 'updateRange',
  selectedRangeOption: 'selectedRangeOption',
};

class AssetDataController {
  constructor($stateParams, $filter, gettextCatalog, $ngRedux) {
    'ngInject';

    this.$stateParams = $stateParams;
    this.$filter = $filter;
    this.gettextCatalog = gettextCatalog;
    this.store = $ngRedux;

    this.maxDate = moment().endOf('minute').toDate();
    this.minDate = null;

    //=====
    this.series = [];
    this.seriesData = {};
    this.visibleSeries = [];
    this.underShowMoreSeries = [];
    this.displaySeries = []; // holed series will be displayed
  }

  $onInit() {
    this.unsubscribe = this.store.connect(() => ({}), { ...AssetDetailsActions })(this);
    //get or refresh data => passing true to update date range
    this.getSeriesData(true);
    this.maxDate = moment().endOf('minute').toDate();
    this.minDate = moment().subtract(30, 'days').startOf('minute').toDate();
  }
  $onDestroy() {
    this.unsubscribe();
  }
  $onChanges(changes) {
    const { asset, seriesData } = changes;
    if (asset && asset.currentValue) {
      const { series = [] } = asset.currentValue;
      // Filter access management from the series
      this.series = series.filter((serie) => !isAccessDataPoint(serie.dataId));

      this.visibleSeries = this.series.filter((series) => series.display);
      this.underShowMoreSeries = this.series.filter((series) => !series.display);
      this.displaySeries = this.getDisplayedSeries();
    }

    if (seriesData && seriesData.currentValue) {
      if (this.series.length) {
        [this.chartData, this.valuesData] = this.getChartAndValuesData(
          this.series,
          this.seriesData,
        );
      }
    }
  }

  addToDataLayer(data) {
    window.dataLayer.push({
      event: EVENT_TRACKING_NAME,
      data: { ...data },
    });
  }

  getChartAndValuesData(seriesList = [], seriesData = {}) {
    let chartData = [];
    let valuesData = [];
    Object.keys(seriesData).forEach((seriesId) => {
      const series = seriesList.find((ser) => ser.id === seriesId);
      if (series && this.isSeriesSelected(seriesId)) {
        const chartDataobject = {
          series: series,
          data: seriesData[seriesId].chartData || [],
          rollup: seriesData[seriesId].chartRollup,
        };
        const valuesDataobject = {
          series: series,
          data: seriesData[seriesId].valuesData || [],
        };
        chartData.push(chartDataobject);
        valuesData.push(valuesDataobject);
      }
    });
    chartData = sortBy(chartData, (seriesData) => seriesData.series.order);
    valuesData = sortBy(valuesData, (seriesData) => seriesData.series.order);
    return [chartData, valuesData];
  }

  showMoreButtonPressed() {
    this.showMoreSelected = !this.showMoreSelected;
    this.displaySeries = this.getDisplayedSeries();
  }

  getDisplayedSeries() {
    return this.showMoreSelected
      ? [...this.visibleSeries, ...this.underShowMoreSeries]
      : [...this.visibleSeries];
  }

  toggleSeries(seriesId) {
    const currentSeriesSelected = this.isSeriesSelected(seriesId);

    if (!currentSeriesSelected) {
      this.selectSeries(seriesId);
    } else {
      this.deselectSeries(seriesId);
    }

    this.addToDataLayer({
      action: EVENT_TRACKING_ACTIONS.toggleSerie,
      isSelected: currentSeriesSelected,
      id: seriesId,
    });
  }

  switchToTab(tabKey) {
    this.setActiveDataTab(tabKey);
  }

  isSeriesSelected(id) {
    return (this.selectedSeriesIds || []).includes(id);
  }

  //=== date range
  updateStartDate(startDate) {
    //fire action to update the date range
    this.setDateRange(startDate, this.dateRange.end);

    this.addToDataLayer({
      action: EVENT_TRACKING_ACTIONS.updateRange,
      startDate: startDate,
      endDate: this.dateRange.end,
    });
  }

  updateEndDate(endDate) {
    //fire action to update the date range
    this.setDateRange(this.dateRange.start, endDate);

    this.addToDataLayer({
      action: EVENT_TRACKING_ACTIONS.updateRange,
      startDate: this.dateRange.start,
      endDate: endDate,
    });
  }

  updateSelectedDateRange(start, end) {
    this.setDateRange(start, end);
  }

  updateDateRangeOption(option) {
    //fire action to update the date range by selected option
    this.setDateRangeByOption(option);

    this.addToDataLayer({
      action: EVENT_TRACKING_ACTIONS.selectedRangeOption,
      optionSelected: option,
    });
  }

  //========== csv ==============
  getCsvData() {
    this.addToDataLayer({
      action: EVENT_TRACKING_ACTIONS.exportCSVClicked,
    });
    const CsvData = [];

    const headers = {
      DataPoint: this.gettextCatalog.getString('Datapoint'),
      Value: this.gettextCatalog.getString('Value'),
      Units: this.gettextCatalog.getString('Unit'),
      LastCommunicated: this.gettextCatalog.getString('Last Communicated'),
    };

    CsvData.push(headers);

    this.series.forEach((series) => {
      const datapointname = this.gettextCatalog.getString(series.title);

      const lastValueObj = series.last;
      let value = lastValueObj
        ? this.$filter('attrsValueTranslate')(lastValueObj.v, series, true)
        : this.gettextCatalog.getString('No data available');

      const lastcommunicated =
        lastValueObj && lastValueObj.t
          ? this.$filter('TableDateFilter')(moment(lastValueObj.t))
          : this.gettextCatalog.getString('No data available');

      const unit = series.attrs.uom ? series.attrs.uom : '';

      const csvObject = {
        DataPoint: datapointname,
        Value: value,
        Unit: unit,
        LastCommunicated: lastcommunicated,
      };
      CsvData.push(csvObject);
    });
    return CsvData;
  }

  getCsvFileName() {
    return `asset-${this.asset.name}.csv`;
  }
}

export default {
  template,
  controller: AssetDataController,
  //not useing vm as when using ui-route it is conflicting with parent vm
  //https://github.com/johnpapa/angular-styleguide/issues/577#issue-115275989
  controllerAs: 'dataVm',
  bindings: {
    asset: '<',
    dateRange: '<',
    seriesDataStatus: '<',
    selectedSeriesIds: '<',
    activeTab: '<',
    seriesData: '<',
  },
};
